import React, { useEffect, useRef } from "react";
import "./GeoChart.scss";

export interface GeoChartData {
  latitude: number;
  longitude: number;
  name: string;
  numberOfSurveys: number;
}

type GeoChartProps = {
  locationList: GeoChartData[];
};

declare global {
  interface Window {
    google: any;
    initInsightsMap: () => void;
  }
}

let mapsLoaded = false;
let mapsCallbacks: (() => void)[] = [];

function loadGoogleMaps(callback: () => void) {
  if (mapsLoaded && window.google?.maps) {
    callback();
    return;
  }
  mapsCallbacks.push(callback);
  if (document.getElementById("google-maps-script")) return;

  window.initInsightsMap = () => {
    mapsLoaded = true;
    mapsCallbacks.forEach((cb) => cb());
    mapsCallbacks = [];
  };

  const script = document.createElement("script");
  script.id = "google-maps-script";
  script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_KEY}&callback=initInsightsMap&loading=async`;
  script.async = true;
  script.defer = true;
  document.head.appendChild(script);
}

export const GeoChart: React.FC<GeoChartProps> = ({ locationList }) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstance = useRef<any>(null);

  useEffect(() => {
    if (!locationList?.length || !mapRef.current) return;

    loadGoogleMaps(() => {
      if (!mapRef.current) return;
      const google = window.google;

      const map = new google.maps.Map(mapRef.current, {
        zoom: 5,
        center: { lat: -41.0, lng: 174.0 },
        mapTypeId: "satellite",
        mapTypeControl: false,
        streetViewControl: false,
      });
      mapInstance.current = map;

      const infoWindow = new google.maps.InfoWindow();

      locationList.forEach((item) => {
        const marker = new google.maps.Marker({
          position: { lat: item.latitude, lng: item.longitude },
          map,
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            scale: 6,
            fillColor: "#F05282",
            fillOpacity: 0.7,
            strokeColor: "#D63D6B",
            strokeWeight: 1,
          },
          title: item.name,
        });

        marker.addListener("click", () => {
          infoWindow.setContent(
            `<div style="min-width: 250px; max-width: 350px; padding: 4px; font-family: sans-serif;">` +
              `<div style="font-size: 14px; font-weight: bold; margin-bottom: 8px; line-height: 1.3;">` +
              `${item.name}</div>` +
              `<div style="font-size: 13px; color: #555;">Surveys completed: ${item.numberOfSurveys}</div>` +
              `</div>`
          );
          infoWindow.open(map, marker);
        });
      });
    });
  }, [locationList]);

  return (
    <div>
      <div className="google_maps_container" ref={mapRef} />
    </div>
  );
};
